import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, CheckboxGroup, Flex, Skeleton, Text, VStack } from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { Checkbox } from '@nocowanie/common-ui/components/atoms/checkbox';

import { FilterCheckboxGroupProps } from '@app/components/molecules/filter-checkbox-group';
import { useSearchFilterCounter } from '@app/hooks/useSearchFilterCounter';

export const FilterCheckboxGroup = ({
    title,
    items,
    selectedValues = [],
    onValueChange,
    initialVisibleCount,
    isLoading = false,
    getPredictedValue,
    prependCheckbox,
    filterKey,
    withCounter,
    ...rest
}: FilterCheckboxGroupProps) => {
    const { t } = useTranslation('common');

    const [localSelectedValues, setLocalSelectedValues] = useState(new Set(selectedValues));
    const { counters, prependCheckboxCounter } = useSearchFilterCounter({
        isEnabled: withCounter,
        items,
        filterKey,
        selectedValue: selectedValues,
        getPredictedValue,
        prependCheckbox,
    });
    const [localCheckboxValue, setLocalCheckboxValue] = useState(prependCheckbox?.checked ?? false);
    const [isExpanded, setIsExpanded] = useState(false);
    const hasAutoExpandedRef = useRef(false);

    useEffect(() => {
        setLocalCheckboxValue(prev => {
            const newChecked = prependCheckbox?.checked ?? false;
            return prev !== newChecked ? newChecked : prev;
        });
    }, [prependCheckbox?.checked]);

    useEffect(() => {
        setLocalSelectedValues(new Set(selectedValues));
    }, [selectedValues]);

    const displayedItems = useMemo(
        () => (initialVisibleCount && !isExpanded ? items.slice(0, initialVisibleCount) : items),
        [items, initialVisibleCount, isExpanded],
    );

    const showToggleButton = initialVisibleCount && items.length > initialVisibleCount;

    const handleValueChange = useCallback(
        (value: string, checked: boolean) => {
            setLocalSelectedValues(prev => {
                const newSelectedValues = new Set(prev);
                if (checked) {
                    newSelectedValues.add(value);
                } else {
                    newSelectedValues.delete(value);
                }
                return newSelectedValues;
            });

            onValueChange(value, checked);
        },
        [onValueChange],
    );

    useEffect(() => {
        if (!initialVisibleCount || hasAutoExpandedRef.current) {
            return;
        }

        const shouldAutoExpand = items.some(
            (item, index) => index >= initialVisibleCount && localSelectedValues.has(item.value),
        );

        if (shouldAutoExpand) {
            setIsExpanded(true);
            hasAutoExpandedRef.current = true;
        }
    }, [localSelectedValues, items, initialVisibleCount]);

    return (
        <Box {...rest}>
            {title ? (
                <Skeleton loading={isLoading} w={'160px'} asChild>
                    <Text fontWeight="bold" mb={4}>
                        {title}
                    </Text>
                </Skeleton>
            ) : null}
            <Skeleton loading={isLoading} asChild>
                <Box>
                    {prependCheckbox ? (
                        <Checkbox
                            value={prependCheckbox.item.value}
                            checked={localCheckboxValue}
                            onCheckedChange={prependCheckbox.onCheckedChange}
                            cursor="pointer"
                            gap={4}
                            mb={4}
                            size="md"
                        >
                            <Flex alignItems={'center'} gap={2}>
                                {prependCheckbox.item.icon ? (
                                    <Box color={'gray.600'} display="flex" alignItems="center">
                                        {prependCheckbox.item.icon}
                                    </Box>
                                ) : null}
                                <Text as={'span'} fontSize={'md'}>
                                    {prependCheckbox.item.label}{' '}
                                    {isNil(prependCheckboxCounter) ? null : (
                                        <Text
                                            as={'span'}
                                            color={'gray.500'}
                                        >{`(${prependCheckboxCounter})`}</Text>
                                    )}
                                </Text>
                            </Flex>
                        </Checkbox>
                    ) : null}

                    <CheckboxGroup as={VStack} align="stretch" gap={4}>
                        {displayedItems.map((item, index) => {
                            const isDisabled =
                                withCounter &&
                                !localSelectedValues.has(item.value) &&
                                typeof counters[index] === 'number' &&
                                counters[index] === 0;
                            return (
                                <Checkbox
                                    disabled={isDisabled}
                                    key={item.value}
                                    checked={localSelectedValues.has(item.value)}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        if (isDisabled) {
                                            return;
                                        }

                                        handleValueChange(
                                            item.value,
                                            !localSelectedValues.has(item.value),
                                        );
                                    }}
                                    cursor={isDisabled ? undefined : 'pointer'}
                                    gap={4}
                                    size="md"
                                >
                                    <Flex alignItems={'center'} gap={2}>
                                        {item.icon ? (
                                            <Box
                                                color={'gray.600'}
                                                display="flex"
                                                alignItems="center"
                                            >
                                                {item.icon}
                                            </Box>
                                        ) : null}
                                        <Text as={'span'} fontSize={'md'}>
                                            {item.label}{' '}
                                            {!withCounter || isNil(counters[index]) ? null : (
                                                <Text
                                                    as={'span'}
                                                    color={'gray.500'}
                                                >{`(${counters[index]})`}</Text>
                                            )}
                                        </Text>
                                    </Flex>
                                </Checkbox>
                            );
                        })}
                    </CheckboxGroup>
                </Box>
            </Skeleton>
            {showToggleButton ? (
                <Skeleton loading={isLoading} asChild>
                    <Button variant="link" onClick={() => setIsExpanded(!isExpanded)} mt={4}>
                        {t(isExpanded ? 'list-button.less' : 'list-button.more')}
                    </Button>
                </Skeleton>
            ) : null}
        </Box>
    );
};
